import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faFilter, faCheckCircle } from '@fortawesome/free-solid-svg-icons';

const GlossaryFilter = ({ 
  isOpen, 
  onClose, 
  selectedFilter, 
  onSelectFilter, 
  availableFilters,
  theme 
}) => {
  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      backdropFilter: 'blur(5px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '1rem',
    },
    modal: {
      background: theme?.background?.card,
      borderRadius: '1.5rem',
      padding: '2rem',
      maxWidth: '500px',
      width: '100%',
      maxHeight: '80vh',
      overflow: 'auto',
      boxShadow: theme?.shadows?.xl,
      border: `1px solid ${theme?.borders?.primary}`,
      position: 'relative',
    },
    closeButton: {
      position: 'absolute',
      top: '1rem',
      right: '1rem',
      width: '2rem',
      height: '2rem',
      borderRadius: '0.5rem',
      border: 'none',
      background: theme?.backgrounds?.secondary,
      color: theme?.text?.secondary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    header: {
      marginBottom: '2rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
    },
    icon: {
      fontSize: '1.5rem',
      color: theme?.primary?.blue?.[500],
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: theme?.text?.primary,
    },
    filterGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(60px, 1fr))',
      gap: '0.75rem',
    },
    filterButton: {
      padding: '0.75rem',
      borderRadius: '0.75rem',
      border: `2px solid ${theme?.borders?.primary}`,
      background: theme?.backgrounds?.secondary,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      textAlign: 'center',
      fontWeight: '500',
      fontSize: '0.875rem',
      color: theme?.text?.primary,
      position: 'relative',
    },
    filterButtonActive: {
      background: theme?.primary?.blue?.[100],
      borderColor: theme?.primary?.blue?.[500],
      color: theme?.primary?.blue?.[700],
    },
    filterButtonAll: {
      gridColumn: 'span 2',
    },
    checkIcon: {
      position: 'absolute',
      top: '0.25rem',
      right: '0.25rem',
      fontSize: '0.75rem',
      color: theme?.primary?.blue?.[500],
    },
    divider: {
      height: '1px',
      background: theme?.borders?.primary,
      margin: '1rem 0',
    },
    infoBox: {
      background: theme?.backgrounds?.secondary,
      borderRadius: '0.75rem',
      padding: '1rem',
      marginTop: '1.5rem',
      fontSize: '0.875rem',
      color: theme?.text?.secondary,
      lineHeight: 1.6,
    },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          style={styles.overlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            style={styles.modal}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", damping: 20 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              style={styles.closeButton}
              onClick={onClose}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            
            <div style={styles.header}>
              <FontAwesomeIcon icon={faFilter} style={styles.icon} />
              <h2 style={styles.title}>Filter by Letter</h2>
            </div>
            
            <div style={styles.filterGrid}>
              {/* ALL filter */}
              <motion.button
                style={{
                  ...styles.filterButton,
                  ...styles.filterButtonAll,
                  ...(selectedFilter === 'ALL' && styles.filterButtonActive),
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => onSelectFilter('ALL')}
              >
                All Terms
                {selectedFilter === 'ALL' && (
                  <FontAwesomeIcon icon={faCheckCircle} style={styles.checkIcon} />
                )}
              </motion.button>
              
              {/* Letter filters */}
              {availableFilters.filter(f => f !== 'ALL').map((filter, index) => {
                const isActive = selectedFilter === filter;
                
                return (
                  <motion.button
                    key={filter}
                    style={{
                      ...styles.filterButton,
                      ...(isActive && styles.filterButtonActive),
                    }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => onSelectFilter(filter)}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.02 }}
                  >
                    {filter}
                    {isActive && (
                      <FontAwesomeIcon icon={faCheckCircle} style={styles.checkIcon} />
                    )}
                  </motion.button>
                );
              })}
            </div>
            
            <div style={styles.infoBox}>
              <strong>Tip:</strong> Use the alphabet filter to quickly find terms starting with a specific letter. 
              The "#" filter shows all numeric entries.
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default GlossaryFilter;